import { Link as TsrLink } from '@tanstack/react-router';
import type React from 'react';
import { graphql, useFragment } from 'react-relay';

import { useZnglyContext } from '@/Utility/context/ZnglyAppContext';
import { Container, Grid2, type Grid2Props, Skeleton, Typography } from '@mui/material';
import type { Footer_SiteInfo$data, Footer_SiteInfo$key } from './__generated__/Footer_SiteInfo.graphql';

import { PAGE_LAYOUT } from '@/inertia/Shared/Page';
import { showPreferences } from 'vanilla-cookieconsent';
import Suspense from '../Suspense';
import UiText from '../ui/Frontend/uiText';

import styles from './footer.module.css';

const FooterFragment = graphql`
  fragment Footer_SiteInfo on ZnglyInfo {
    znglyInfo {
      email
      privacyUserConsentLevel
      address1
      address2
      address3
      linksTitle
      links {
        linkName
        linkUrl
      }
      phone1
      phone2
    }
  }
`;

type Props = {
  style?: React.CSSProperties;
  znglyInfo: Footer_SiteInfo$data['znglyInfo'];
};

const FooterComponent = ({ style, znglyInfo }: Props) => {
  const gridProps: Grid2Props = {
    size: {
      xs: 6,
      md: 3,
    },
  };

  return (
    <footer className={styles.footer} style={style}>
      <Container {...PAGE_LAYOUT.containerProps}>
        <Grid2 container spacing={1}>
          <Grid2 {...gridProps}>
            <h3>{znglyInfo?.linksTitle}</h3>
            <ul>
              {znglyInfo?.links?.map((link) => (
                <li key={`${link?.linkName}_${link?.linkUrl}`}>
                  <UiText title={link?.linkName ?? ''} href={link?.linkUrl ?? ''} />
                </li>
              ))}
            </ul>
          </Grid2>
          <Grid2 {...gridProps}>
            <h3>Privacy</h3>
            <ul>
              <li>
                <Typography component={TsrLink} to="/privacy-policy">
                  Privacy Policy
                </Typography>
              </li>
              <li hidden={znglyInfo?.privacyUserConsentLevel === 'DISABLED'}>
                <Typography
                  sx={{
                    cursor: 'pointer',
                  }}
                  onClick={showPreferences}
                >
                  Manage cookie preferences
                </Typography>
              </li>
            </ul>
          </Grid2>
          <Grid2 {...gridProps}>
            <h3>Address</h3>
            <ul>
              <li>
                <UiText title={znglyInfo?.address1?.toString()} />
              </li>
              <li>
                <UiText title={znglyInfo?.address2?.toString()} />
              </li>
              <li>
                <UiText title={znglyInfo?.address3?.toString()} />
              </li>
            </ul>
          </Grid2>
          <Grid2 {...gridProps}>
            <h3>Contact</h3>
            <ul>
              <li>
                <UiText title={znglyInfo?.email?.toString()} href={znglyInfo?.email} />
              </li>
              <li>
                <UiText title={znglyInfo?.phone1?.toString()} href={znglyInfo?.phone1} />
              </li>
              <li>
                <UiText title={znglyInfo?.phone2?.toString()} href={znglyInfo?.phone2} />
              </li>
            </ul>
          </Grid2>
        </Grid2>
      </Container>
    </footer>
  );
};

type HeaderInputProps = {
  style?: React.CSSProperties;
};

const FooterFallback = (props: HeaderInputProps) => {
  const Skelet = () => <Skeleton sx={{ bgcolor: 'var(--footer-s-text-col)' }} />;
  const MultiSkelet = () => (
    <>
      {[...Array(3).keys()].map((_) => (
        <li key={_}>
          <Skelet />
        </li>
      ))}
    </>
  );

  const gridElements = [null, 'Privacy', 'Address', 'Contact'].map((title, idx) => (
    <Grid2 size={{ sm: 6, md: 3, lg: 3, xl: 3 }} key={title}>
      <h3>{title ?? <Skelet />}</h3>
      <ul>
        <MultiSkelet />
      </ul>
    </Grid2>
  ));

  return (
    <footer className={styles.footer} {...props}>
      <Container maxWidth="xl">
        <Grid2 container spacing={1}>
          {gridElements}
        </Grid2>
      </Container>
    </footer>
  );
};

const Wrapper = (
  props: HeaderInputProps & {
    queryRef: NonNullable<ReturnType<typeof useZnglyContext>['appData']['queryRef']>['znglyInfo'];
  }
) => {
  const data = useFragment<Footer_SiteInfo$key>(FooterFragment, props.queryRef);

  return <FooterComponent {...props} znglyInfo={data?.znglyInfo} />;
};

export const Footer = (props: HeaderInputProps) => {
  const ctx = useZnglyContext();

  return (
    <Suspense fallback={<FooterFallback style={props.style} />} exists={ctx?.appData?.queryRef?.znglyInfo}>
      {!!ctx?.appData?.queryRef?.znglyInfo && <Wrapper {...props} queryRef={ctx.appData.queryRef.znglyInfo} />}
    </Suspense>
  );
};
